import { cssColour, dispose, observeScene, openRenderer, showFallback } from "./renderer.js";
import { mountResource, type ResourceScope } from "./lifecycle.js";
import { COMPACT_QUERY } from "./mobile.js";
import { launchVelocity, type SandboxTool, type WorldPoint } from "../world/interaction.js";
/**
 * Le monde en trois dimensions (Three.js) — **l'application, pas une section**.
 *
 * C'est ici que se joue le renversement du 10 août 2026. Auparavant une vue 3D
 * était un encart au milieu d'une page, montée et démontée à chaque changement
 * d'onglet : on feuilletait des documents illustrés. Cette vue-ci occupe
 * l'écran, ne se démonte jamais, et les scénarios y entrent sans qu'elle
 * s'interrompe. Changer de scénario ne change pas de page : ça change le monde
 * qu'on regarde.
 *
 * ## Le temps
 *
 * On avance d'un **nombre entier de pas par image**, jamais d'un intervalle
 * d'horloge (CLAUDE.md §5). Une machine lente exécute la même suite d'états,
 * plus lentement, au lieu d'avaler un gros pas et de diverger. La vitesse agit
 * sur le nombre de pas, jamais sur leur taille — et le banc du monde vérifie
 * qu'un découpage en images quelconque donne exactement la même trajectoire.
 *
 * ## La caméra
 *
 * Elle appartient à l'utilisateur, sauf quand il demande le contraire. Suivre
 * un corps déplace la **cible** vers lui et translate la caméra du même
 * vecteur : l'angle et la distance choisis à la main sont préservés, seul le
 * point regardé glisse. Reprendre la souris ne rend pas la main de force — le
 * suivi se coupe explicitement, parce qu'un suivi qui s'annule tout seul se lit
 * comme une panne.
 *
 * ## Ce que la vue ne prétend pas
 *
 * Ni les tailles, ni les couleurs ne sont physiques — seules les **distances**
 * le sont. Et rien ici n'est confronté à quoi que ce soit : un monde simulé
 * montre les conséquences d'une loi qu'on lui a donnée, ce qui ne dit rien du
 * monde réel (SPEC §10).
 */

import type { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import type {
  Frame,
  SceneEntityRequest,
  SceneEntitySketch,
  WorldScene,
} from "./world-scene.js";
import {
  approach,
  buildWorldScene,
  fillTrail,
  fitDistance,
  focusDistance,
  frameFor,
  selectionRadius,
  visibleLabels,
} from "./world-scene.js";
import { TRAIL_LENGTH } from "../world/trails.js";
import { escape, escapeAttribute } from "./format.js";
import { fr } from "../i18n/fr.js";

type ThreeModule = typeof import("three");

const FIELD_OF_VIEW = 42;
/**
 * Plans de coupe, en unités de scène.
 *
 * Volontairement extrêmes : le tampon logarithmique les supporte, et c'est ce
 * qui permet d'avoir dans la même image une planète sous le nez et une étoile
 * à cent mille rayons.
 */
const NEAR_PLANE = 1e-4;
const FAR_PLANE = 1e12;
/** Bornes de la molette, en unités de scène. */
const MINIMUM_ORBIT = 1.2e-3;
const MAXIMUM_ORBIT = 1e9;
/**
 * Taille apparente minimale d'un corps, en pixels de **rayon**.
 *
 * Réglé sur ce qu'une capture a montré : à trois pixels, les corps étaient
 * exacts et inutilisables — on ne les voyait pas et on ne pouvait pas les
 * viser. C'est une borne d'ergonomie, et le seul endroit du dessin qui ne soit
 * pas fidèle ; la légende le dit.
 */
const MINIMUM_PIXELS = 9;
/**
 * Recul initial, en multiples de l'étendue du monde.
 *
 * Réglé sur ce qu'une capture a montré : à 1,9 l'orbite la plus extérieure
 * sortait du cadre des trois côtés. Le cadrage doit contenir le monde entier,
 * sans quoi le premier regard laisse croire qu'il manque quelque chose.
 */
const START_DISTANCE = 2.6;
const START_ELEVATION = 0.85;
/** Fraction du chemin restant parcourue par image, pour le suivi et l'approche. */
const TRAVEL_FRACTION = 0.12;
/** Décalage vertical par corps, pour que deux sillages ne se disputent pas le plan. */
const LIFT_PER_BODY = 0.0004;
/** Le halo diffus d'une étoile, en multiples de son rayon dessiné. */
const GLOW_RATIO = 2.1;
/** Distance en pixels entre un corps et son étiquette. */
const LABEL_OFFSET = 14;
/** En deçà de cet écart, deux étiquettes se superposent : la moins prioritaire se tait. */
const LABEL_GAP_X = 72;
const LABEL_GAP_Y = 15;
/** Dix degrés par frappe : visible sans désorienter. */
const KEYBOARD_ORBIT_STEP = Math.PI / 18;
const KEYBOARD_ZOOM_IN = 0.8;
const KEYBOARD_ZOOM_OUT = 1 / KEYBOARD_ZOOM_IN;

/** Ce que la vue reçoit du monde. Elle décide seule du repère et de la caméra. */
export interface WorldSnapshot {
  readonly selectedId: string;
  /** Corps auquel le repère est accroché, ou chaîne vide pour la vue d'ensemble. */
  readonly focusId: string;
  /** Mètres par unité de scène en vue d'ensemble. */
  readonly systemScale: number;
  readonly entities: readonly SceneEntityRequest[];
}

export interface WorldPlayback {
  readonly confirmBeforeCommit?: () => boolean;
  readonly draftChanged?: (ready: boolean) => void;
  readonly suspended?: () => boolean;
  readonly tool?: () => SandboxTool;
  readonly gesture?: (start: WorldPoint, end: WorldPoint) => void;
  readonly preview?: (speed: number) => void;
  /** Le monde à dessiner, tel qu'il est maintenant. */
  readonly snapshot: () => WorldSnapshot;
  /** Avance le monde d'une image. */
  readonly advance: () => void;
  readonly running: () => boolean;
  /** Sélectionne un corps, ou rien si l'identifiant est vide. */
  readonly select: (id: string) => void;
  /** Demande que le repère s'accroche à ce corps — un double-clic dans la vue. */
  readonly focus: (id: string) => void;
  /** Appelé après chaque image : c'est là que l'état remonte, à basse fréquence. */
  readonly synchronise: () => void;
}

export interface WorldHandle {
  readonly confirm: () => void;
  /** Vrai si le renderer est monté ; faux s'il échoue ou est démonté avant montage. */
  readonly ready: Promise<boolean>;
  /** Redessine sans avancer — après une intervention de l'utilisateur. */
  readonly redraw: () => void;
  /** Recadre sur l'ensemble du monde. Appelé au chargement d'un scénario. */
  readonly reframe: () => void;
  /** Amène la caméra devant un corps. */
  readonly lookAt: (id: string) => void;
  readonly detach: () => void;
}

// ── Coquille ─────────────────────────────────────────────────────────────────

export function renderWorldStage(): string {
  return [
    `<div class="verlet-world" data-world-stage role="group" tabindex="0"`,
    ` aria-roledescription="${escapeAttribute(fr.worldSceneRole)}"`,
    ` aria-label="${escapeAttribute(fr.worldSceneAlt)}"`,
    ` aria-describedby="verlet-world-help"`,
    ` aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown Home + -">`,
    `<p class="verlet-world__fallback">${escape(fr.worldLoading)}</p>`,
    `</div>`,
    `<div class="verlet-world__labels" data-world-labels aria-hidden="true"></div>`,
  ].join("");
}

// ── Montage ──────────────────────────────────────────────────────────────────

export function mountWorld(root: ParentNode, playback: WorldPlayback): WorldHandle {
  const lifecycle = mountResource((scope) => attach(root, playback, scope), (error) => {
    console.error(error);
    const stage = root.querySelector<HTMLElement>("[data-world-stage]");
    if (stage) showFallback(stage, fr.worldUnavailable);
  });
  const redraw = (): void => lifecycle.command("redraw", (api) => api.redraw());
  redraw();
  return {
    ready: lifecycle.ready,
    redraw,
    confirm: () => lifecycle.command("confirm", (api) => api.confirm()),
    reframe: () => lifecycle.command("reframe", (api) => api.reframe()),
    lookAt: (id) => lifecycle.command("lookAt", (api) => api.lookAt(id)),
    detach: lifecycle.detach,
  };
}

interface Built {
  readonly group: InstanceType<ThreeModule["Group"]>;
  readonly spheres: Map<string, InstanceType<ThreeModule["Mesh"]>>;
  readonly halos: Map<string, InstanceType<ThreeModule["Mesh"]>>;
  readonly glows: Map<string, InstanceType<ThreeModule["Mesh"]>>;
  readonly trails: Map<string, Trail>;
  readonly labels: Map<string, HTMLElement>;
}

/** Une ligne de sillage et le tampon qu'elle dessine, alloué une seule fois. */
interface Trail {
  readonly line: InstanceType<ThreeModule["Line"]>;
  readonly buffer: Float32Array;
  readonly attribute: InstanceType<ThreeModule["BufferAttribute"]>;
}

interface Api {
  readonly confirm: () => void;
  readonly redraw: () => void;
  readonly reframe: () => void;
  readonly lookAt: (id: string) => void;
}

interface WorldLayer {
  readonly update: (world: WorldScene) => void;
  readonly positionLabels: (world: WorldScene, width: number, height: number) => void;
  readonly pick: (event: MouseEvent) => string | undefined;
  readonly invalidate: () => void;
  readonly dispose: () => void;
}

function createWorldLayer(
  three: ThreeModule,
  scene: InstanceType<ThreeModule["Scene"]>,
  camera: InstanceType<ThreeModule["PerspectiveCamera"]>,
  key: InstanceType<ThreeModule["DirectionalLight"]>,
  renderer: InstanceType<ThreeModule["WebGLRenderer"]>,
  labelHost: HTMLElement,
): WorldLayer {
  let content = new three.Group();
  scene.add(content);
  let built: Built = {
    group: content,
    spheres: new Map(),
    halos: new Map(),
    glows: new Map(),
    trails: new Map(),
    labels: new Map(),
  };
  let signature = "";

  const raycaster = new three.Raycaster();
  const pointer = new three.Vector2();
  const projected = new three.Vector3();
  const projectedCentre = new three.Vector3();

  const place = (world: WorldScene): void => {
    world.entities.forEach((entity, rank) => {
      const lift = rank * LIFT_PER_BODY;
      const sphere = built.spheres.get(entity.id);
      if (sphere !== undefined) {
        sphere.position.set(entity.centre.x, entity.centre.y, entity.centre.z);
        sphere.scale.setScalar(entity.radius);
      }

      const glow = built.glows.get(entity.id);
      if (glow !== undefined) {
        glow.position.set(entity.centre.x, entity.centre.y, entity.centre.z);
        glow.scale.setScalar(entity.radius * GLOW_RATIO);
      }

      const halo = built.halos.get(entity.id);
      if (halo !== undefined) {
        halo.position.set(entity.centre.x, entity.centre.y, entity.centre.z);
        halo.scale.setScalar(selectionRadius(entity));
        halo.visible = entity.selected;
      }

      const trail = built.trails.get(entity.id);
      if (trail !== undefined) {
        const count = fillTrail(trail.buffer, entity.trail, lift);
        trail.attribute.needsUpdate = true;
        trail.line.geometry.setDrawRange(0, count);
        trail.line.geometry.computeBoundingSphere();
      }
    });
  };

  const lightFromStar = (world: WorldScene): void => {
    let best: SceneEntitySketch | undefined;
    let bestDistance = Infinity;
    for (const entity of world.entities) {
      if (entity.kind !== "star") continue;
      const distance = Math.hypot(entity.centre.x, entity.centre.z);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = entity;
      }
    }
    if (best === undefined || bestDistance < 1e-9) return;
    key.position.set(best.centre.x, best.centre.y, best.centre.z);
  };

  return {
    update: (world) => {
      const nextSignature = world.entities
        .map((entity) => `${entity.id}:${entity.kind}`)
        .join("|");
      if (nextSignature !== signature) {
        scene.remove(content);
        dispose(content);
        for (const label of built.labels.values()) label.remove();
        built = build(three, world, labelHost);
        content = built.group;
        signature = nextSignature;
        scene.add(content);
      }
      place(world);
      lightFromStar(world);
    },
    positionLabels: (world, width, height) => {
      const candidates: { id: string; x: number; y: number; priority: number }[] = [];
      for (const entity of world.entities) {
        projectedCentre.set(entity.centre.x, entity.centre.y, entity.centre.z);
        projected.copy(projectedCentre);
        projected.project(camera);
        if (projected.z > 1) continue;
        const screenRadius =
          (entity.radius / Math.max(camera.position.distanceTo(projectedCentre), 1e-9) /
            Math.tan((FIELD_OF_VIEW * Math.PI) / 360)) *
          (height / 2);
        candidates.push({
          id: entity.id,
          x: ((projected.x + 1) / 2) * width,
          y:
            ((1 - projected.y) / 2) * height +
            Math.min(screenRadius, height / 2) +
            LABEL_OFFSET,
          priority: (entity.selected ? 1000 : 0) + entity.radius,
        });
      }

      const shown = visibleLabels(candidates, LABEL_GAP_X, LABEL_GAP_Y);
      for (const entity of world.entities) {
        const label = built.labels.get(entity.id);
        if (label === undefined) continue;
        const placed = candidates.find((candidate) => candidate.id === entity.id);
        if (placed === undefined || !shown.has(entity.id)) {
          label.style.display = "none";
          continue;
        }
        label.style.display = "";
        label.style.transform =
          `translate(-50%, 0) translate(${String(placed.x)}px, ${String(placed.y)}px)`;
        label.setAttribute("data-selected", entity.selected ? "true" : "false");
      }
    },
    pick: (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const targets = [...built.spheres.entries()];
      const hits = raycaster.intersectObjects(
        targets.map(([, mesh]) => mesh),
        false,
      );
      const first = hits[0];
      if (first !== undefined) return targets.find(([, mesh]) => mesh === first.object)?.[0];
      if (!window.matchMedia(COMPACT_QUERY).matches) return;
      let closest: string | undefined;
      let distance = 22;
      for (const [id, mesh] of targets) {
        projected.copy(mesh.position).project(camera);
        if (projected.z < -1 || projected.z > 1) continue;
        const pixels = Math.hypot((projected.x + 1) * rect.width / 2 - (event.clientX - rect.left),
          (1 - projected.y) * rect.height / 2 - (event.clientY - rect.top));
        if (pixels < distance) { closest = id; distance = pixels; }
      }
      return closest;
    },
    invalidate: () => {
      signature = "";
    },
    dispose: () => {
      scene.remove(content);
      dispose(content);
      for (const label of built.labels.values()) label.remove();
    },
  };
}

interface CameraController {
  readonly frameOf: (snapshot: WorldSnapshot) => Frame;
  readonly frameOnce: (world: WorldScene, snapshot: WorldSnapshot) => void;
  readonly update: () => void;
  readonly resetFrame: () => void;
  readonly travelTo: (snapshot: WorldSnapshot, id: string) => void;
  readonly orbit: (azimuth: number, polar: number) => void;
  readonly zoom: (factor: number) => void;
}

function createCameraController(
  camera: InstanceType<ThreeModule["PerspectiveCamera"]>,
  controls: OrbitControls,
): CameraController {
  let anchoredTo = "";
  let currentScale = 1;
  let travelDistance: number | undefined;
  let framed = false;

  const reframeOn = (distance: number): void => {
    camera.position.set(distance * 0.1, distance * START_ELEVATION, distance);
    camera.position.setLength(distance);
    controls.target.set(0, 0, 0);
    controls.update();
    travelDistance = undefined;
  };

  return {
    frameOf: (snapshot) => {
      const anchor = snapshot.entities.find((entity) => entity.id === snapshot.focusId);
      const next = frameFor(
        anchor === undefined ? undefined : { position: anchor.position, radius: anchor.radius },
        snapshot.systemScale,
      );
      if (snapshot.focusId !== anchoredTo) {
        const metres = camera.position.length() * currentScale;
        const converted = metres / next.scale;
        camera.position.setLength(
          Math.min(Math.max(converted, MINIMUM_ORBIT), MAXIMUM_ORBIT),
        );
        anchoredTo = snapshot.focusId;
        travelDistance = undefined;
      }
      currentScale = next.scale;
      return next;
    },
    frameOnce: (world, snapshot) => {
      if (framed) return;
      const anchored = world.entities.find((entity) => entity.id === snapshot.focusId);
      const distance =
        anchored === undefined
          ? world.extent * START_DISTANCE
          : Math.max(focusDistance(anchored.radius), MINIMUM_ORBIT);
      reframeOn(fitDistance(distance, camera.aspect));
      framed = true;
    },
    update: () => {
      if (travelDistance !== undefined) {
        const from = camera.position.length();
        const next = approach(
          { x: from, y: 0, z: 0 },
          { x: travelDistance, y: 0, z: 0 },
          TRAVEL_FRACTION,
        ).x;
        camera.position.setLength(next);
        if (next === travelDistance) travelDistance = undefined;
      }
      controls.update();
    },
    resetFrame: () => {
      framed = false;
    },
    travelTo: (snapshot, id) => {
      const entity = snapshot.entities.find((candidate) => candidate.id === id);
      if (entity === undefined) return;
      const scale = frameFor(
        { position: entity.position, radius: entity.radius },
        snapshot.systemScale,
      ).scale;
      travelDistance = fitDistance(
        Math.max(focusDistance(entity.radius / scale), MINIMUM_ORBIT),
        camera.aspect,
      );
    },
    orbit: (azimuth, polar) => {
      const radius = camera.position.length();
      if (radius <= 0) return;
      const theta = Math.atan2(camera.position.x, camera.position.z) + azimuth;
      const currentPolar = Math.acos(Math.min(Math.max(camera.position.y / radius, -1), 1));
      const nextPolar = Math.min(Math.max(currentPolar + polar, 0.05), Math.PI - 0.05);
      const horizontal = radius * Math.sin(nextPolar);
      camera.position.set(
        horizontal * Math.sin(theta),
        radius * Math.cos(nextPolar),
        horizontal * Math.cos(theta),
      );
      travelDistance = undefined;
      controls.update();
    },
    zoom: (factor) => {
      camera.position.setLength(
        Math.min(Math.max(camera.position.length() * factor, MINIMUM_ORBIT), MAXIMUM_ORBIT),
      );
      travelDistance = undefined;
      controls.update();
    },
  };
}

async function attach(
  root: ParentNode,
  playback: WorldPlayback,
  scope: ResourceScope,
): Promise<Api | undefined> {
  const stage = root.querySelector<HTMLElement>("[data-world-stage]");
  const labelHost = root.querySelector<HTMLElement>("[data-world-labels]");
  if (stage === null || labelHost === null) return;

  const opened = await openRenderer(stage, scope, { logarithmicDepthBuffer: true });
  if (!opened) return;
  const { three, renderer } = opened;
  const { OrbitControls } = await import("three/examples/jsm/controls/OrbitControls.js");
  if (scope.disposed) return;
  const compactScreen = window.matchMedia(COMPACT_QUERY);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, compactScreen.matches ? 1.5 : 2));

  const scene = new three.Scene();
  scope.own(() => dispose(scene));
  const camera = new three.PerspectiveCamera(FIELD_OF_VIEW, 1, NEAR_PLANE, FAR_PLANE);
  scene.add(camera);
  // L'ambiante est faible et la lumière principale vient de l'étoile : de près,
  // une planète montre alors un terminateur au lieu d'un disque uniforme. Ce
  // n'est pas qu'une joliesse — c'est la même géométrie qui donnera l'ombre
  // d'un gnomon le jour où Ératosthène entrera dans le monde.
  scene.add(new three.AmbientLight(0xffffff, 1.1));
  const key = new three.DirectionalLight(0xffffff, 1.5);
  key.position.set(0.5, 1, 0.6);
  scene.add(key);

  const controls = new OrbitControls(camera, renderer.domElement);
  scope.own(() => controls.dispose());
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  // La cible ne bouge jamais : c'est le **repère** qui se déplace sous elle.
  // Suivre un corps revient alors à poser l'origine sur lui, et le suivi ne
  // coûte plus rien — ni translation de caméra, ni dérive accumulée.
  controls.target.set(0, 0, 0);
  controls.minDistance = MINIMUM_ORBIT;
  controls.maxDistance = MAXIMUM_ORBIT;

  const layer = createWorldLayer(three, scene, camera, key, renderer, labelHost);
  scope.own(layer.dispose);
  const cameraController = createCameraController(camera, controls);

  let pendingEdit: { start: WorldPoint; end: WorldPoint } | undefined;
  let anchor: WorldPoint | undefined;
  let hover: WorldPoint | undefined;
  let previousTool: SandboxTool = "select";
  const arrow = new three.ArrowHelper(new three.Vector3(1, 0, 0), new three.Vector3(), 1, cssColour("--accent", "#8cdcff"));
  arrow.visible = false;
  scene.add(arrow);
  const marker = new three.Mesh(new three.SphereGeometry(1, 16, 12),
    new three.MeshBasicMaterial({ color: cssColour("--accent", "#8cdcff"), transparent: true, opacity: 0.55 }));
  marker.visible = false;
  scene.add(marker);
  const placementRay = new three.Raycaster();
  const orbitalPlane = new three.Plane(new three.Vector3(0, 1, 0), 0);
  const hit = new three.Vector3();
  const pointAt = (event: MouseEvent): WorldPoint | undefined => {
    const rect = renderer.domElement.getBoundingClientRect();
    placementRay.setFromCamera(new three.Vector2(
      (event.clientX - rect.left) / rect.width * 2 - 1,
      -(event.clientY - rect.top) / rect.height * 2 + 1), camera);
    if (placementRay.ray.intersectPlane(orbitalPlane, hit) === null) return;
    const frame = cameraController.frameOf(playback.snapshot());
    return { x: hit.x * frame.scale + frame.origin.x, y: hit.z * frame.scale + frame.origin.y };
  };

  const draw = (): void => {
    const width = stage.clientWidth;
    const height = stage.clientHeight;
    if (width === 0 || height === 0) return;

    const snapshot = playback.snapshot();
    const frame = cameraController.frameOf(snapshot);

    // Le plancher angulaire vient de la géométrie de la projection : un corps
    // doit occuper au moins tant de pixels, et c'est la vue seule qui sait
    // combien elle en a.
    const minimumAngle =
      ((compactScreen.matches ? 12 : MINIMUM_PIXELS) / height) * 2 * Math.tan((FIELD_OF_VIEW * Math.PI) / 360);

    const world = buildWorldScene({
      frame,
      camera: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
      minimumAngle,
      selectedId: snapshot.selectedId,
      entities: snapshot.entities,
    });

    const tool = playback.tool?.() ?? "select";
    controls.enabled = tool === "select";
    renderer.domElement.style.cursor = tool === "select" ? "grab" : "crosshair";
    if (tool !== previousTool) {
      anchor = undefined;
      hover = undefined;
      pendingEdit = undefined;
      playback.draftChanged?.(false);
      previousTool = tool;
    }
    if (tool === "velocity") anchor = snapshot.entities.find((one) => one.id === snapshot.selectedId)?.position;
    marker.visible = tool !== "select" && (anchor ?? hover) !== undefined;
    const point = anchor ?? hover;
    if (point !== undefined) {
      marker.position.set((point.x - frame.origin.x) / frame.scale, 0, (point.y - frame.origin.y) / frame.scale);
      marker.scale.setScalar(camera.position.length() * minimumAngle);
    }
    arrow.visible = tool !== "select" && anchor !== undefined && hover !== undefined;
    if (arrow.visible && anchor !== undefined && hover !== undefined) {
      const delta = new three.Vector3((hover.x - anchor.x) / frame.scale, 0, (hover.y - anchor.y) / frame.scale);
      const length = delta.length();
      arrow.position.copy(marker.position);
      if (length > 1e-9) {
        arrow.setDirection(delta.normalize());
        arrow.setLength(length, Math.min(length * 0.25, camera.position.length() * 0.025), Math.min(length * 0.12, camera.position.length() * 0.012));
      } else arrow.visible = false;
    }
    layer.update(world);

    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();

    cameraController.frameOnce(world, snapshot);
    cameraController.update();
    renderer.render(scene, camera);
    layer.positionLabels(world, width, height);
  };

  // ── La boucle ────────────────────────────────────────────────────────────
  let frameHandle = 0;
  let dirty = true;
  const invalidateCamera = (): void => { dirty = true; };
  controls.addEventListener("change", invalidateCamera);
  scope.own(() => controls.removeEventListener("change", invalidateCamera));
  scope.own(() => window.cancelAnimationFrame(frameHandle));

  const tick = (): void => {
    frameHandle = window.requestAnimationFrame(tick);
    if (document.hidden || playback.suspended?.()) return;
    if (playback.running()) playback.advance();
    if (playback.running() || dirty) {
      dirty = false;
      draw();
      playback.synchronise();
    }
  };
  frameHandle = window.requestAnimationFrame(tick);

  // ── La sélection ─────────────────────────────────────────────────────────
  let pressedAt = { x: 0, y: 0 };
  let touchStart: WorldPoint | undefined;
  let suppressClick = false;
  const pointers = new Set<number>();
  let multiTouch = false;
  const onPointerDown = (event: PointerEvent): void => {
    pointers.add(event.pointerId);
    if (pointers.size > 1) { multiTouch = true; suppressClick = true; return; }
    suppressClick = false;
    pressedAt = { x: event.clientX, y: event.clientY };
    touchStart = pointAt(event);
    if (event.pointerType === "touch" && (playback.tool?.() ?? "select") !== "select") {
      renderer.domElement.setPointerCapture(event.pointerId);
    }
  };
  const onPointerMove = (event: PointerEvent): void => {
    const tool = playback.tool?.() ?? "select";
    if (tool === "select" || multiTouch) return;
    if (pendingEdit !== undefined && event.buttons === 0) return;
    if (event.pointerType === "touch" && anchor === undefined && (tool === "planet" || tool === "star")
      && Math.hypot(event.clientX - pressedAt.x, event.clientY - pressedAt.y) > 8) anchor = touchStart;
    hover = pointAt(event);
    if (anchor !== undefined && hover !== undefined) {
      const velocity = launchVelocity(anchor, hover);
      playback.preview?.(Math.hypot(velocity.x, velocity.y));
    }
    // Un déplacement du doigt ne valide jamais un état précédent.
    if (pendingEdit !== undefined && hover !== undefined) {
      pendingEdit = { start: tool === "move" ? hover : pendingEdit.start, end: hover };
    }
    draw();
  };
  const commitPoint = (point: WorldPoint): void => {
    const tool = playback.tool?.() ?? "select";
    if (tool !== "move" && anchor === undefined) { anchor = point; hover = point; }
    else {
      const start = tool === "move" ? point : anchor!;
      if (playback.confirmBeforeCommit?.()) {
        pendingEdit = { start, end: point };
        anchor = start;
        hover = point;
        playback.draftChanged?.(true);
      } else { playback.gesture?.(start, point); anchor = undefined; hover = undefined; }
    }
    draw();
  };
  const onPointerUp = (event: PointerEvent): void => {
    pointers.delete(event.pointerId);
    if (multiTouch) { if (pointers.size === 0) multiTouch = false; return; }
    if (event.pointerType !== "touch" || (playback.tool?.() ?? "select") === "select") return;
    suppressClick = true;
    const point = pointAt(event);
    if (point !== undefined) commitPoint(point);
  };
  const onPointerCancel = (): void => {
    pointers.clear(); multiTouch = false; touchStart = undefined; suppressClick = true;
    pendingEdit = undefined; anchor = undefined; hover = undefined;
    playback.draftChanged?.(false);
    draw();
  };
  const onClick = (event: MouseEvent): void => {
    if (suppressClick) { suppressClick = false; return; }
    if (Math.hypot(event.clientX - pressedAt.x, event.clientY - pressedAt.y) > 6) return;
    const tool = playback.tool?.() ?? "select";
    if (tool === "select") { playback.select(layer.pick(event) ?? ""); return; }
    const point = pointAt(event);
    if (point !== undefined) commitPoint(point);
  };
  const onDoubleClick = (event: MouseEvent): void => {
    if ((playback.tool?.() ?? "select") !== "select") return;
    const id = layer.pick(event);
    if (id === undefined) return;
    playback.select(id);
    goTo(id);
  };
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if ((playback.tool?.() ?? "select") !== "select") {
      const frame = cameraController.frameOf(playback.snapshot());
      const cursor = hover ?? anchor ?? frame.origin;
      const step = frame.scale * (event.shiftKey ? 0.5 : 0.1);
      if (event.key === "Enter") { event.preventDefault(); commitPoint(cursor); return; }
      if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) {
        event.preventDefault();
        hover = { x: cursor.x + (event.key === "ArrowRight" ? step : event.key === "ArrowLeft" ? -step : 0),
          y: cursor.y + (event.key === "ArrowDown" ? step : event.key === "ArrowUp" ? -step : 0) };
        if (anchor !== undefined) {
          const velocity = launchVelocity(anchor, hover);
          playback.preview?.(Math.hypot(velocity.x, velocity.y));
        }
        draw();
      }
      return;
    }
    const command = (() => {
      if (event.key === "ArrowLeft") return () => cameraController.orbit(-KEYBOARD_ORBIT_STEP, 0);
      if (event.key === "ArrowRight") return () => cameraController.orbit(KEYBOARD_ORBIT_STEP, 0);
      if (event.key === "ArrowUp") return () => cameraController.orbit(0, -KEYBOARD_ORBIT_STEP);
      if (event.key === "ArrowDown") return () => cameraController.orbit(0, KEYBOARD_ORBIT_STEP);
      if (event.key === "+" || event.key === "=") return () => cameraController.zoom(KEYBOARD_ZOOM_IN);
      if (event.key === "-" || event.key === "_") return () => cameraController.zoom(KEYBOARD_ZOOM_OUT);
      if (event.key === "Home") return () => cameraController.resetFrame();
      return undefined;
    })();
    if (command === undefined) return;
    event.preventDefault();
    command();
    draw();
  };

  /**
   * Aller voir un corps : accrocher le repère sur lui, puis n'ajuster que la
   * **distance**.
   *
   * La direction depuis laquelle on regardait est conservée — arriver toujours
   * du même côté désorienterait. Et comme le corps devient l'origine du repère,
   * il n'y a plus de cible à déplacer : la caméra ne fait que se rapprocher.
   */
  const goTo = (id: string): void => {
    playback.focus(id);
    cameraController.travelTo(playback.snapshot(), id);
  };

  renderer.domElement.addEventListener("pointerdown", onPointerDown);
  renderer.domElement.addEventListener("pointermove", onPointerMove);
  renderer.domElement.addEventListener("pointerup", onPointerUp);
  renderer.domElement.addEventListener("pointercancel", onPointerCancel);
  renderer.domElement.addEventListener("click", onClick);
  renderer.domElement.addEventListener("dblclick", onDoubleClick);
  stage.addEventListener("keydown", onKeyDown);

  scope.own(() => {
    renderer.domElement.removeEventListener("pointerdown", onPointerDown);
    renderer.domElement.removeEventListener("pointermove", onPointerMove);
    renderer.domElement.removeEventListener("pointerup", onPointerUp);
    renderer.domElement.removeEventListener("pointercancel", onPointerCancel);
    renderer.domElement.removeEventListener("click", onClick);
    renderer.domElement.removeEventListener("dblclick", onDoubleClick);
    stage.removeEventListener("keydown", onKeyDown);
  });

  const onResize = (): void => {
    // Le champ horizontal dépend du ratio de la vue. Le premier avis arrive
    // quand la mise en page est stabilisée ; les suivants quand elle change.
    // Dans les deux cas, conserver l'ancien recul peut cadrer sur un seul
    // corps. On refait donc le même cadrage que la touche Home.
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, compactScreen.matches ? 1.5 : 2));
    cameraController.resetFrame();
    draw();
  };
  const onThemeChange = (): void => {
    arrow.setColor(new three.Color(cssColour("--accent", "#8cdcff")));
    marker.material.color.set(cssColour("--accent", "#8cdcff"));
    layer.invalidate();
    draw();
  };
  observeScene(scope, stage, onResize, onThemeChange);

  return {
    confirm: () => {
      if (pendingEdit === undefined) return;
      const edit = pendingEdit;
      pendingEdit = undefined;
      playback.draftChanged?.(false);
      playback.gesture?.(edit.start, edit.end);
      anchor = undefined; hover = undefined;
      draw();
    },
    redraw: draw,
    reframe: () => {
      cameraController.resetFrame();
      draw();
    },
    lookAt: goTo,
  };
}

// ── Construction ─────────────────────────────────────────────────────────────

const PALETTE = ["--series-a", "--series-b", "--series-c", "--accent", "--ink-soft"] as const;
const FALLBACKS = ["#2c5f5a", "#8a4b12", "#4a4668", "#b4622a", "#5c584f"] as const;

/**
 * Une étoile émet, une planète reçoit.
 *
 * C'est la seule chose que la nature d'un corps commande. Elle ne touche ni à
 * sa taille ni à sa position, qui sont les deux seules grandeurs que ce dessin
 * prétend rendre — et un test le vérifie.
 *
 * La teinte de rang est **éclaircie** pour une étoile plutôt que remplacée : la
 * remplacer rendrait les deux étoiles d'un système binaire indiscernables,
 * alors qu'un rang leur donne à chacune la sienne. Sans cet éclaircissement,
 * une étoile en matériau non éclairé prenait la couleur brute du rang et
 * s'affichait comme un disque sombre — ce qu'aucune étoile ne fait.
 */
function materialFor(
  three: ThreeModule,
  entity: SceneEntitySketch,
  colour: InstanceType<ThreeModule["Color"]>,
): InstanceType<ThreeModule["Material"]> {
  if (entity.kind !== "star") return new three.MeshLambertMaterial({ color: colour });
  const bright = colour.clone().lerp(new three.Color(1, 1, 1), 0.55);
  return new three.MeshBasicMaterial({ color: bright });
}

/** Le halo diffus d'une étoile : ce qui la fait lire comme une source. */
function glowFor(
  three: ThreeModule,
  entity: SceneEntitySketch,
  colour: InstanceType<ThreeModule["Color"]>,
): InstanceType<ThreeModule["Mesh"]> | undefined {
  if (entity.kind !== "star") return undefined;
  return new three.Mesh(
    new three.SphereGeometry(1, 20, 14),
    new three.MeshBasicMaterial({ color: colour, transparent: true, opacity: 0.18 }),
  );
}

function build(three: ThreeModule, world: WorldScene, labelHost: HTMLElement): Built {
  const group = new three.Group();
  const spheres = new Map<string, InstanceType<ThreeModule["Mesh"]>>();
  const halos = new Map<string, InstanceType<ThreeModule["Mesh"]>>();
  const glows = new Map<string, InstanceType<ThreeModule["Mesh"]>>();
  const trails = new Map<string, Trail>();
  const labels = new Map<string, HTMLElement>();

  const ink = new three.Color(cssColour("--ink", "#1b1a17"));

  world.entities.forEach((one, rank) => {
    const index = rank % PALETTE.length;
    const colour = new three.Color(
      cssColour(PALETTE[index] ?? "--ink", FALLBACKS[index] ?? "#1b1a17"),
    );

    // Le tampon est alloué à sa taille maximale et ne bouge plus : `setFromPoints`
    // à chaque image ne remplissait rien du tout (voir `fillTrail`).
    const buffer = new Float32Array(TRAIL_LENGTH * 3);
    const attribute = new three.BufferAttribute(buffer, 3);
    attribute.setUsage(three.DynamicDrawUsage);
    const geometry = new three.BufferGeometry();
    geometry.setAttribute("position", attribute);
    geometry.setDrawRange(0, 0);

    const line = new three.Line(
      geometry,
      new three.LineBasicMaterial({ color: colour, transparent: true, opacity: 0.6 }),
    );
    group.add(line);
    trails.set(one.id, { line, buffer, attribute });

    const sphere = new three.Mesh(
      new three.SphereGeometry(1, 28, 20),
      materialFor(three, one, colour),
    );
    group.add(sphere);
    spheres.set(one.id, sphere);

    const glow = glowFor(three, one, colour);
    if (glow !== undefined) {
      group.add(glow);
      glows.set(one.id, glow);
    }

    // Le corps sélectionné porte un halo, pas seulement une autre couleur : la
    // sélection doit se lire sans distinguer les teintes (CLAUDE.md §3).
    const halo = new three.Mesh(
      new three.SphereGeometry(1, 20, 14),
      new three.MeshBasicMaterial({
        color: ink,
        wireframe: true,
        transparent: true,
        opacity: 0.5,
      }),
    );
    halo.visible = one.selected;
    group.add(halo);
    halos.set(one.id, halo);

    const label = document.createElement("span");
    label.className = "verlet-label";
    label.textContent = one.label;
    labelHost.append(label);
    labels.set(one.id, label);
  });

  return { group, spheres, halos, glows, trails, labels };
}
